import { gql } from "@apollo/client";

export const GET_ALL_LISTINGS = gql`
  query {
    getAllListings {
      _id
      acreage
      baths
      beds
      city
      company {
        _id
        companyName
      }
      user {
        id
        firstName
        lastName
        phone
        email
        licenseNo
      }
      amenities {
        _id
      }
      office {
        _id
        name
      }
      streetAddr
      state
      sqFt
      price
      propertyType
      propertyCategory
      expiration
      lotSize
      yearBuilt
      zip
      notes
      createdAt
      coordinates
      openHouse {
        startTime
        endTime
        date
        needHost
      }
      openHouses {
        _id
        startTime
        endTime
        needHost
      }
    }
  }
`;

export const GET_ALL_OPEN_HOUSES_LISTINGS = gql`
  query {
    getAllOpenHousesListings {
      _id
      acreage
      baths
      beds
      city
      company {
        _id
        companyName
      }
      user {
        id
        firstName
        lastName
        phone
        email
        licenseNo
      }
      amenities {
        _id
      }
      office {
        _id
        name
      }
      streetAddr
      state
      sqFt
      price
      propertyType
      propertyCategory
      expiration
      lotSize
      yearBuilt
      zip
      notes
      createdAt
      coordinates
      openHouse {
        startTime
        endTime
        date
        needHost
      }
      openHouses {
        _id
        startTime
        endTime
        needHost
      }
    }
  }
`;

export const GET_COMPANY_LISTINGS = gql`
  query (
    $companyId: String!
    $pagination: pagination
    $searchText: String
    $sort: sort
  ) {
    getCompanyListings(
      companyId: $companyId
      pagination: $pagination
      searchText: $searchText
      sort: $sort
    ) {
      totalCount
      listing {
        _id
        acreage
        baths
        beds
        city
        company {
          _id
          companyName
        }
        user {
          id
          firstName
          lastName
          phone
          email
          licenseNo
        }
        amenities {
          _id
        }
        office {
          _id
          name
        }
        streetAddr
        state
        sqFt
        price
        propertyType
        propertyCategory
        expiration
        lotSize
        yearBuilt
        zip
        notes
        createdAt
        coordinates
        imageURLs
        openHouse {
          startTime
          endTime
          date
          needHost
        }
        openHouses {
          _id
          startTime
          endTime
          needHost
        }
        links {
          title
          url
        }
      }
    }
  }
`;

// Upcoming Listings (admin). Asks for everything a card or a presentation slide
// can show, so the same query backs the list, the detail view and Meeting Mode
// without a second round trip.
export const GET_UPCOMING_LISTINGS = gql`
  query getUpcomingListings(
    $companyId: String!
    $listingInput: FilteringListingInput
    $pagination: pagination
    $searchText: String
    $sortBy: String
  ) {
    getCompanyListings(
      companyId: $companyId
      listingInput: $listingInput
      pagination: $pagination
      searchText: $searchText
      sortBy: $sortBy
    ) {
      totalCount
      listing {
        _id
        streetAddr
        city
        state
        zip
        price
        beds
        baths
        sqFt
        lotSize
        acreage
        yearBuilt
        propertyType
        propertyCategory
        buyer_commission
        expiration
        createdAt
        notes
        coordinates
        imageURLs
        user {
          id
          firstName
          lastName
          phone
          email
          licenseNo
        }
        office {
          _id
          name
        }
        amenities {
          _id
          title
        }
        openHouses {
          _id
          startTime
          endTime
          needHost
        }
        links {
          title
          url
        }
      }
    }
  }
`;

// One upcoming listing, with everything the detail view and a presentation
// slide can show. Kept separate from GET_LISTING_BY_ID, which the edit form
// uses and which deliberately asks for ids rather than display fields.
export const GET_UPCOMING_LISTING_DETAIL = gql`
  query getUpcomingListingDetail($listingId: String) {
    getListingById(listingId: $listingId) {
      _id
      streetAddr
      city
      state
      zip
      price
      beds
      baths
      sqFt
      lotSize
      acreage
      yearBuilt
      propertyType
      propertyCategory
      buyer_commission
      expiration
      createdAt
      notes
      coordinates
      imageURLs
      company {
        _id
      }
      user {
        id
        firstName
        lastName
        phone
        email
        licenseNo
      }
      office {
        _id
        name
      }
      amenities {
        _id
        title
      }
      openHouses {
        _id
        startTime
        endTime
        needHost
      }
      links {
        title
        url
      }
      changes {
        key
        value
        modified
      }
    }
  }
`;

export const GET_LISTING_BY_ID = gql`
  query ($listingId: String) {
    getListingById(listingId: $listingId) {
      _id
      expiration
      streetAddr
      city
      state
      zip
      beds
      baths
      yearBuilt
      price
      sqFt
      lotSize
      acreage
      buyer_commission
      propertyType
      propertyCategory
      company {
        _id
      }
      user {
        id
      }
      notes
      createdAt
      deletedAt
      office {
        _id
      }
      amenities {
        _id
      }
      coordinates
      imageURLs
      openHouse {
        startTime
        endTime
        date
        needHost
      }
      openHouses {
        _id
        startTime
        endTime
        needHost
      }
      links {
        title
        url
      }
    }
  }
`;

export const ADD_LISTING = gql`
  mutation ($listingInput: ListingInput) {
    addListing(listingInput: $listingInput) {
      _id
      city
      streetAddr
      state
      zip
      beds
      baths
      sqFt
      lotSize
      acreage
      price
      createdAt
      expiration
      notes
      yearBuilt
      propertyType
      propertyCategory
      openHouse {
        startTime
        endTime
        date
        needHost
      }
    }
  }
`;

export const UPDATE_LISTING = gql`
  mutation ($listingInput: ListingInput) {
    updateListing(listingInput: $listingInput) {
      _id
      city
      streetAddr
      state
      zip
      beds
      baths
      sqFt
      lotSize
      acreage
      price
      propertyType
      propertyCategory
      createdAt
      expiration
      notes
      yearBuilt
      openHouse {
        startTime
        endTime
        date
        needHost
      }
    }
  }
`;

export const DELETE_LISTING = gql`
  mutation ($listingId: String) {
    deleteListing(listingId: $listingId) {
      _id
    }
  }
`;

export const GET_SIMILAR_ADDRESSES = gql`
  query ($address: String!) {
    autocomplete(address: $address) {
      description
      placeId
    }
  }
`;

export const GET_DETAILED_ADDRESS = gql`
  query ($placeId: String!) {
    getDetailedAddress(placeId: $placeId) {
      city
      shortAddress
      state
      zipCode
    }
  }
`;

export const BULK_LISTING_UPLOAD = gql`
  mutation ($listingBulk: [ListingBulk]) {
    bulkListingUpload(listingBulk: $listingBulk) {
      totalSaved
      notSaved {
        StreetAddr
        City
      }
    }
  }
`;

// Upcoming open houses, one row per slot. Drives both the dashboard button
// (which reads only the counts) and the Open Houses tab.
export const GET_COMPANY_UPCOMING_OPEN_HOUSES = gql`
  query getCompanyUpcomingOpenHouses(
    $companyId: String!
    $withinDays: Int
    $needsHostOnly: Boolean
    $agentId: String
    $sortBy: String
  ) {
    getCompanyUpcomingOpenHouses(
      companyId: $companyId
      withinDays: $withinDays
      needsHostOnly: $needsHostOnly
      agentId: $agentId
      sortBy: $sortBy
    ) {
      totalCount
      needsHostCount
      rows {
        listingId
        openHouseId
        startTime
        endTime
        needHost
        host {
          id
          firstName
          lastName
        }
        listing {
          _id
          streetAddr
          city
          state
          zip
          price
          imageURLs
          user {
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`;

// Counts only — the dashboard button never renders the list.
export const GET_OPEN_HOUSE_HOST_COUNTS = gql`
  query getCompanyUpcomingOpenHouses($companyId: String!, $withinDays: Int) {
    getCompanyUpcomingOpenHouses(companyId: $companyId, withinDays: $withinDays) {
      totalCount
      needsHostCount
    }
  }
`;

// hostId: null unassigns.
export const ASSIGN_OPEN_HOUSE_HOST = gql`
  mutation assignOpenHouseHost(
    $listingId: String!
    $openHouseId: String!
    $hostId: String
  ) {
    assignOpenHouseHost(
      listingId: $listingId
      openHouseId: $openHouseId
      hostId: $hostId
    ) {
      _id
      openHouses {
        _id
        startTime
        endTime
        needHost
        host {
          id
          firstName
          lastName
        }
      }
    }
  }
`;
